import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalSuccess, swalError, swalConfirm } from '../utils/alert';
import './CardList.css';
import { Modal } from 'react-bootstrap';

function ProviderList({ onEdit, refreshTrigger, onNewProvider }) {
  const [providers, setProviders] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    document: '',
    type: '',
    status: '',
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const width = useWindowWidth();

  const fetchProviders = (url = '/providers', currentFilters = filters) => {
    const params = new URLSearchParams(currentFilters).toString();
    const fullUrl = `${url.split('?')[0]}?${params}`;

    api.get(fullUrl)
      .then(response => {
        setProviders(response.data.data || response.data); // Adjust for pagination or direct array
        setPagination(response.data.links ? response.data : null);
      })
      .catch(error => {
        console.error('Houve um erro ao buscar os fornecedores!', error);
      });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilters = () => {
    fetchProviders('/providers');
    if (width < 768) {
      setShowFilterModal(false);
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      name: '',
      document: '',
      type: '',
      status: '',
    };
    setFilters(clearedFilters);
    fetchProviders('/providers', clearedFilters);
    if (width < 768) {
      setShowFilterModal(false);
    }
  };

  useEffect(() => {
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const handleDelete = async (id) => {
    const result = await swalConfirm({ text: 'Tem certeza que deseja excluir este fornecedor?' });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/providers/${id}`);
      swalSuccess('Fornecedor excluído com sucesso!');
      fetchProviders(pagination?.current_page_url || '/providers');
    } catch (error) {
      console.error('Houve um erro ao excluir o fornecedor!', error);
      swalError('Erro ao excluir fornecedor.');
    }
  };

  const decodePaginationLabel = (label) => {
    return label
      .replace(/&laquo;/g, '«')
      .replace(/&raquo;/g, '»')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  const renderPaginationLinks = () => {
    if (!pagination || !pagination.links) {
      return null;
    }

    return (
        <nav>
            <ul className="pagination">
                {pagination.links.map((link, index) => (
                    <li key={index} className={`page-item ${link.active ? 'active' : ''} ${!link.url ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => {
                                if (link.url) {
                                    const url = new URL(link.url);
                                    const params = new URLSearchParams(filters).toString();
                                    fetchProviders(`${url.pathname}?${params}&page=${url.searchParams.get('page')}`);
                                }
                            }}
                        >
                            {decodePaginationLabel(link.label)}
                        </button>
                    </li>
                ))}
            </ul>
        </nav>
    );
  };

  const renderFilters = () => (
    <div className="card card-body mb-3">
        <div className="row">
          <div className="col-md-6">
            <label>Nome/Razão Social</label>
            <input type="text" name="name" value={filters.name} onChange={handleFilterChange} className="form-control" />
          </div>
          <div className="col-md-6">
            <label>Documento</label>
            <input type="text" name="document" value={filters.document} onChange={handleFilterChange} className="form-control" />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-6">
            <label>Tipo</label>
            <select name="type" value={filters.type} onChange={handleFilterChange} className="form-control">
              <option value="">Todos</option>
              <option value="Fornecedor">Fornecedor</option>
              <option value="Prestador de Serviço">Prestador de Serviço</option>
              <option value="Ambos">Ambos</option>
            </select>
          </div>
          <div className="col-md-6">
            <label>Status</label>
            <select name="status" value={filters.status} onChange={handleFilterChange} className="form-control">
              <option value="">Todos</option>
              <option value="Ativo">Ativo</option>
              <option value="Inativo">Inativo</option>
            </select>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-12">
            <button className="btn btn-primary me-2" onClick={handleApplyFilters}>Aplicar Filtros</button>
            <button className="btn btn-secondary" onClick={handleClearFilters}>Limpar Filtros</button>
          </div>
        </div>
      </div>
  );

  const renderMobileView = () => (
    <div>
      <button className="btn btn-primary mb-3" onClick={() => setShowFilterModal(true)}>Filtros</button>
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} dialogClassName="filters-modal">
        <Modal.Header closeButton>
          <Modal.Title>Filtros</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderFilters()}
        </Modal.Body>
      </Modal>
      
      {providers.map(provider => (
        <div key={provider.id} className="card-list-card">
          <div className="card-list-card-header">
            <h3>{provider.name}</h3>
            <span className="card-list-card-amount">{provider.document}</span>
          </div>
          <div className="card-list-card-body">
            <p><strong>Tipo:</strong> {provider.type}</p>
            <p><strong>Status:</strong> {provider.status}</p>
            <p><strong>Email:</strong> {provider.email}</p>
          </div>
          <div className="card-list-card-footer">
            <button className="btn btn-warning btn-sm me-2" onClick={() => onEdit(provider)}>Editar</button>
            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(provider.id)}>Excluir</button>
          </div>
        </div>
      ))}
      {renderPaginationLinks()}
      <button className="fab" onClick={onNewProvider}>+</button>
    </div>
  );

  const renderDesktopView = () => (
    <div>
      <button className="btn btn-primary mb-3" onClick={() => setShowFilters(!showFilters)}>
          {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
      </button>
      {showFilters && renderFilters()}
      <table className="table table-striped table-hover">
        <thead className="thead-dark">
          <tr>
            <th>Nome/Razão Social</th>
            <th>Documento</th>
            <th>Tipo</th>
            <th>Email</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {providers.map(provider => (
            <tr key={provider.id}>
              <td>{provider.name}</td>
              <td>{provider.document}</td>
              <td>{provider.type}</td>
              <td>{provider.email}</td>
              <td>{provider.status}</td>
              <td>
                <button className="btn btn-warning btn-sm me-2" onClick={() => onEdit(provider)}>Editar</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(provider.id)}>Excluir</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {renderPaginationLinks()}
    </div>
  );

  return (
    <div>
        <h2>Fornecedores</h2>
        {width < 768 ? renderMobileView() : renderDesktopView()}
    </div>
  );
}

export default ProviderList;